import React from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  Grid,
  VStack,
  HStack,
  Icon,
  Progress,
  Card,
  CardBody,
  Heading,
  SimpleGrid,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { 
  FiAlertCircle, 
  FiClock, 
  FiUser, 
  FiCalendar, 
  FiCode, 
  FiTarget, 
  FiZap, 
  FiCheckCircle,
  FiAlertTriangle,
  FiTrendingUp,
  FiSettings,
  FiShield,
  FiActivity,
  FiGitBranch
} from 'react-icons/fi';

export default function BugTicketDetails({ issueKey, ticket, aiAnalysis }) {
  // Color mode values - must be at top level
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  
  try {
    // Use the actual ticket data passed as props, with AI analysis data when available
    const ticketDetails = ticket ? {
      issueKey: ticket.id || issueKey,
      title: ticket.title || ticket.summary || aiAnalysis?.bugTitle || "No title available",
      severity: ticket.severity || aiAnalysis?.severity || aiAnalysis?.bugSeverity || "Medium",
      status: ticket.status || aiAnalysis?.status || aiAnalysis?.bugStatus || "Open",
      priority: ticket.priority || "P2",
      assignee: ticket.assignee || aiAnalysis?.assignee || "Unassigned",
      reporter: ticket.reporter || ticket.createdBy || "Unknown",
      created: ticket.created ? new Date(ticket.created).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }) : aiAnalysis?.createdAt ? new Date(aiAnalysis.createdAt).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }) : "Unknown",
      updated: ticket.updated ? new Date(ticket.updated).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }) : aiAnalysis?.updatedAt ? new Date(aiAnalysis.updatedAt).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }) : "Unknown",
      // Use AI analysis data when available, fallback to ticket data
      aiSummary: aiAnalysis?.summary || aiAnalysis?.detailedSummary || aiAnalysis?.aiAnalysis || aiAnalysis?.aiSummary || ticket.aiAnalysis || ticket.aiSummary || ticket.description || "No AI analysis available for this issue.",
      rootCause: aiAnalysis?.rootCauseReasoning || aiAnalysis?.rootCause || aiAnalysis?.primaryRootCause || aiAnalysis?.cause || ticket.rootCause || ticket.cause || "Unknown",
      impact: (() => {
        // Handle different possible formats for impact
        if (aiAnalysis?.businessImpact) {
          if (typeof aiAnalysis.businessImpact === 'string') {
            return aiAnalysis.businessImpact;
          }
          if (typeof aiAnalysis.businessImpact === 'object' && aiAnalysis.businessImpact !== null) {
            const impact = aiAnalysis.businessImpact;
            let impactText = '';
            if (impact.clientNames && impact.clientNames.length > 0) {
              impactText += `Clients: ${impact.clientNames.join(', ')}. `;
            }
            if (impact.employeeIds && impact.employeeIds.length > 0) {
              impactText += `Employees affected: ${impact.employeeIds.length}. `;
            }
            if (impact.userCount !== undefined) {
              impactText += `Total users: ${impact.userCount}. `;
            }
            if (impact.impactType) {
              impactText += `Impact type: ${impact.impactType}. `;
            }
            if (impact.scope) {
              impactText += `Scope: ${impact.scope}. `;
            }
            if (impact.urgency) {
              impactText += `Urgency: ${impact.urgency}.`;
            }
            return impactText || JSON.stringify(impact);
          }
        }
        if (aiAnalysis?.clientAffected) {
          return aiAnalysis.clientAffected;
        }
        if (ticket.impact) {
          if (typeof ticket.impact === 'string') {
            return ticket.impact;
          }
          if (typeof ticket.impact === 'object' && ticket.impact !== null) {
            return JSON.stringify(ticket.impact);
          }
        }
        return "Impact assessment not available";
      })(),
      immediateActions: aiAnalysis?.recommendations?.immediateActions || [],
      modulesAffected: (() => {
        // Handle different possible formats for modules affected
        if (aiAnalysis?.leaveSystemAnalysis?.affectedModules && Array.isArray(aiAnalysis.leaveSystemAnalysis.affectedModules)) {
          return aiAnalysis.leaveSystemAnalysis.affectedModules;
        }
        if (aiAnalysis?.allCategories && typeof aiAnalysis.allCategories === 'object') {
          return Object.keys(aiAnalysis.allCategories);
        }
        if (aiAnalysis?.module) {
          return aiAnalysis.module.split(', ').map(m => m.trim());
        }
        if (aiAnalysis?.modulesAffected && Array.isArray(aiAnalysis.modulesAffected)) {
          return aiAnalysis.modulesAffected;
        }
        if (aiAnalysis?.affectedModules && Array.isArray(aiAnalysis.affectedModules)) {
          return aiAnalysis.affectedModules;
        }
        if (Array.isArray(ticket.modulesAffected)) {
          return ticket.modulesAffected;
        }
        if (Array.isArray(ticket.affectedModules)) {
          return ticket.affectedModules;
        }
        if (ticket.module) {
          return [ticket.module];
        }
        return ["Unknown"];
      })(),
      customersAffected: (() => {
        // Handle different possible formats for customers affected
        if (aiAnalysis?.leaveSystemAnalysis?.employeeImpactDetails?.affectedEmployees && Array.isArray(aiAnalysis.leaveSystemAnalysis.employeeImpactDetails.affectedEmployees)) {
          return aiAnalysis.leaveSystemAnalysis.employeeImpactDetails.affectedEmployees;
        }
        if (aiAnalysis?.client) {
          return [aiAnalysis.client];
        }
        if (aiAnalysis?.customersAffected && Array.isArray(aiAnalysis.customersAffected)) {
          return aiAnalysis.customersAffected;
        }
        if (aiAnalysis?.affectedCustomers && Array.isArray(aiAnalysis.affectedCustomers)) {
          return aiAnalysis.affectedCustomers;
        }
        if (aiAnalysis?.clientImpact) {
          // If clientImpact is an object, extract relevant fields
          if (typeof aiAnalysis.clientImpact === 'object' && aiAnalysis.clientImpact !== null) {
            if (aiAnalysis.clientImpact.clientNames && Array.isArray(aiAnalysis.clientImpact.clientNames)) {
              return aiAnalysis.clientImpact.clientNames;
            }
            if (aiAnalysis.clientImpact.name) {
              return [aiAnalysis.clientImpact.name];
            }
            // If it's an object but we can't extract a name, convert to string
            return [JSON.stringify(aiAnalysis.clientImpact)];
          }
          // If it's a string, wrap in array
          if (typeof aiAnalysis.clientImpact === 'string') {
            return [aiAnalysis.clientImpact];
          }
        }
        if (Array.isArray(ticket.customersAffected)) {
          return ticket.customersAffected;
        }
        if (Array.isArray(ticket.affectedCustomers)) {
          return ticket.affectedCustomers;
        }
        if (ticket.client) {
          return [ticket.client];
        }
        return ["Unknown"];
      })(),
      recurrencePattern: aiAnalysis?.recurrencePattern || aiAnalysis?.pattern || ticket.recurrencePattern || ticket.pattern || "Recurrence pattern not analyzed",
      recurrenceProbability: parseInt(aiAnalysis?.recurrenceProbability || aiAnalysis?.rootCauseScore || aiAnalysis?.recurrenceRisk || ticket.recurrenceProbability || ticket.recurrenceRisk || 50),
      techDetails: {
        errorCode: aiAnalysis?.errorCode || aiAnalysis?.code || ticket.errorCode || ticket.code || "N/A",
        affectedEndpoints: aiAnalysis?.affectedEndpoints || aiAnalysis?.endpoints ||
                          Array.isArray(ticket.affectedEndpoints) ? ticket.affectedEndpoints :
                          Array.isArray(ticket.endpoints) ? ticket.endpoints :
                          ticket.endpoint ? [ticket.endpoint] : ["No endpoints specified"],
        reproductionSteps: (() => {
          if (aiAnalysis?.recommendations?.detailed?.immediateActions?.qualityAssurance) {
            return aiAnalysis.recommendations.detailed.immediateActions.qualityAssurance;
          }
          if (aiAnalysis?.qualityAssurance) {
            return aiAnalysis.qualityAssurance;
          }
          if (aiAnalysis?.developerAnalysis?.stepsToReproduce) {
            return aiAnalysis.developerAnalysis.stepsToReproduce;
          }
          if (aiAnalysis?.reproductionSteps) {
            return aiAnalysis.reproductionSteps;
          }
          if (aiAnalysis?.steps) {
            return aiAnalysis.steps;
          }
          if (ticket.reproductionSteps) {
            return ticket.reproductionSteps;
          }
          if (ticket.steps) {
            return ticket.steps;
          }
          return "No QA testing plan available";
        })(),
        environmentsAffected: aiAnalysis?.environmentsAffected || aiAnalysis?.environments ||
                           Array.isArray(ticket.environmentsAffected) ? ticket.environmentsAffected :
                           Array.isArray(ticket.environments) ? ticket.environments :
                           ticket.environment ? [ticket.environment] : ["Unknown"],
        fixEta: aiAnalysis?.technicalAnalysis?.estimatedEffort || aiAnalysis?.fixEta || aiAnalysis?.eta || ticket.fixEta || ticket.eta || "Not specified"
      },
      developerAnalysis: {
        rootCauseAnalysis: aiAnalysis?.developerAnalysis?.rootCauseAnalysis || aiAnalysis?.rootCauseReasoning || aiAnalysis?.analysis || aiAnalysis?.primaryRootCause || ticket.rootCauseAnalysis || ticket.analysis || "No detailed analysis available",
        fixApplied: aiAnalysis?.fixApplied || ticket.status === "Done" || ticket.status === "Resolved" || ticket.status === "Closed",
        fixDetails: aiAnalysis?.recommendations?.detailed?.immediateActions || aiAnalysis?.recommendations?.immediateActions || aiAnalysis?.fixDetails || aiAnalysis?.solution || aiAnalysis?.suggestedAction || ticket.fixDetails || ticket.solution || ticket.suggestedAction || "No solution proposed yet"
      },
      // Additional AI analysis fields
      executiveSummary: aiAnalysis?.executiveSummary,
      mitigationUrgency: aiAnalysis?.mitigationUrgency,
      systemStabilityImpact: aiAnalysis?.systemStabilityImpact,
      dataIntegrityRisk: aiAnalysis?.dataIntegrityRisk,
      escalationTriggers: aiAnalysis?.escalationTriggers,
      alternativeTheories: aiAnalysis?.alternativeTheories,
      contextualInsights: aiAnalysis?.contextualInsights,
      contributingFactors: aiAnalysis?.contributingFactors || [],
      keywords: aiAnalysis?.keywords || aiAnalysis?.geminiKeywords,
      sentiment: aiAnalysis?.sentiment,
      technicalAnalysis: aiAnalysis?.technicalAnalysis
    } : {};

    // Helper function to safely convert values to strings
    const safeToString = (value) => {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return 'Complex object';
        }
      }
      return String(value);
    };

    // Helper function to get severity color scheme
    const getSeverityColorScheme = (severity) => {
      const sev = severity?.toLowerCase();
      if (sev === 'critical' || sev === 'high') return 'red';
      if (sev === 'medium') return 'orange';
      if (sev === 'low') return 'yellow';
      return 'gray';
    };

    // Helper function to get status color scheme
    const getStatusColorScheme = (status) => {
      const stat = status?.toLowerCase();
      if (stat === 'done' || stat === 'resolved' || stat === 'closed' || stat === 'fixed') return 'green';
      if (stat === 'in progress' || stat === 'working') return 'blue';
      if (stat === 'pending' || stat === 'open' || stat === 'new') return 'yellow';
      return 'gray';
    };

    // Helper function to get recurrence color scheme
    const getRecurrenceColorScheme = (probability) => {
      if (probability >= 80) return 'red';
      if (probability >= 60) return 'orange';
      if (probability >= 40) return 'yellow';
      return 'green';
    };

    return (
      <Box maxW="6xl" mx="auto" bg={bgColor} minH="100vh">
        {/* Compact Header Section */}
        <Box 
          bgGradient="linear(to-r, gray.50, blue.50)" 
          borderBottom="1px" 
          borderColor={borderColor} 
          px={{ base: 4, md: 6 }} 
          py={4}
        >
          <Flex direction="column" gap={3}>
            <Flex direction={{ base: "column", sm: "row" }} align={{ base: "start", sm: "center" }} gap={3}>
              <Heading size="md" color={textColor}>{ticketDetails.issueKey}</Heading>
              <HStack spacing={2} flexWrap="wrap">
                <Badge 
                  colorScheme={getStatusColorScheme(ticketDetails.status)}
                  variant="subtle"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  fontSize="xs"
                >
                  {(ticketDetails.status === 'In Progress' || ticketDetails.status === 'in_progress' || ticketDetails.status === 'working') && <Icon as={FiZap} w={3} h={3} />}
                  {(ticketDetails.status === 'Done' || ticketDetails.status === 'Resolved' || ticketDetails.status === 'Closed' || ticketDetails.status === 'Fixed') && <Icon as={FiCheckCircle} w={3} h={3} />}
                  {(ticketDetails.status === 'Pending' || ticketDetails.status === 'Open' || ticketDetails.status === 'New') && <Icon as={FiClock} w={3} h={3} />}
                  {ticketDetails.status}
                </Badge>
                <Badge 
                  colorScheme={getSeverityColorScheme(ticketDetails.severity)}
                  variant="subtle"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  fontSize="xs"
                >
                  <Icon as={FiAlertTriangle} w={3} h={3} />
                  {ticketDetails.severity}
                </Badge>
                <Badge colorScheme="purple" variant="subtle" display="flex" alignItems="center" gap={1} fontSize="xs">
                  <Icon as={FiTarget} w={3} h={3} />
                  {ticketDetails.priority}
                </Badge>
              </HStack>
            </Flex>
            
            <Text fontSize="lg" color={mutedTextColor} fontWeight="medium" lineHeight="tight" noOfLines={2}>
              {ticketDetails.title}
            </Text>
            
            <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={4}>
              <Flex align="center" gap={2}>
                <Icon as={FiUser} w={4} h={4} color={mutedTextColor} />
                <Box minW={0} flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color={mutedTextColor} textTransform="uppercase" letterSpacing="wide">
                    Assignee
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={1}>
                    {ticketDetails.assignee}
                  </Text>
                </Box>
              </Flex>
              <Flex align="center" gap={2}>
                <Icon as={FiUser} w={4} h={4} color={mutedTextColor} />
                <Box minW={0} flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color={mutedTextColor} textTransform="uppercase" letterSpacing="wide">
                    Reporter
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={1}>
                    {ticketDetails.reporter}
                  </Text>
                </Box>
              </Flex>
              <Flex align="center" gap={2}>
                <Icon as={FiCalendar} w={4} h={4} color={mutedTextColor} />
                <Box minW={0} flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color={mutedTextColor} textTransform="uppercase" letterSpacing="wide">
                    Created
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={1}>
                    {ticketDetails.created}
                  </Text>
                </Box>
              </Flex>
              <Flex align="center" gap={2}>
                <Icon as={FiClock} w={4} h={4} color={mutedTextColor} />
                <Box minW={0} flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color={mutedTextColor} textTransform="uppercase" letterSpacing="wide">
                    Updated
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={1}>
                    {ticketDetails.updated}
                  </Text>
                </Box>
              </Flex>
            </SimpleGrid>
          </Flex>
        </Box>

        {/* Main Content - Compact Grid Layout */}
        <VStack spacing={4} p={{ base: 4, md: 6 }} align="stretch">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
            {/* Left Column - Main Analysis */}
            <VStack spacing={4} align="stretch">
              {/* AI Analysis Summary */}
              <Card bg="blue.50" border="1px" borderColor="blue.200" borderRadius="xl" shadow="sm">
                <CardBody p={4}>
                  <Flex align="center" gap={3} mb={3}>
                    <Box 
                      w={6} 
                      h={6} 
                      bg="blue.500" 
                      borderRadius="lg" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Icon as={FiActivity} w={3} h={3} color="white" />
                    </Box>
                    <Heading size="sm" color="blue.900">AI Analysis Summary</Heading>
                  </Flex>
                  <Text color="blue.800" lineHeight="relaxed" fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                    {ticketDetails.aiSummary}
                  </Text>
                  {ticketDetails.executiveSummary && (
                    <Box mt={3} p={3} bg="blue.100" borderRadius="md">
                      <Text fontSize="xs" fontWeight="bold" color="blue.900" mb={1}>Executive Summary:</Text>
                      <Text fontSize="xs" color="blue.800" whiteSpace="pre-wrap" wordBreak="break-word">{ticketDetails.executiveSummary}</Text>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* Impact Assessment */}
              <Card bg="red.50" border="1px" borderColor="red.200" borderRadius="xl" shadow="sm">
                <CardBody p={4}>
                  <Flex align="center" gap={3} mb={3}>
                    <Box 
                      w={6} 
                      h={6} 
                      bg="red.500" 
                      borderRadius="lg" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Icon as={FiShield} w={3} h={3} color="white" />
                    </Box>
                    <Heading size="sm" color="red.900">Impact Assessment</Heading>
                  </Flex>
                  <Text color="red.800" lineHeight="relaxed" fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                    {ticketDetails.impact}
                  </Text>
                  {ticketDetails.immediateActions && Array.isArray(ticketDetails.immediateActions) && ticketDetails.immediateActions.length > 0 && (
                    <Box mt={3}>
                      <Text fontSize="xs" fontWeight="bold" color="red.900" mb={2}>Immediate Actions:</Text>
                      <List spacing={2}>
                        {ticketDetails.immediateActions.map((action, index) => (
                          <ListItem key={index} fontSize="xs" color="red.800">
                            <ListIcon as={FiAlertCircle} color="red.500" />
                            <Text whiteSpace="pre-wrap" wordBreak="break-word">{action}</Text>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  {ticketDetails.mitigationUrgency && (
                    <Box mt={3} p={3} bg="red.100" borderRadius="md">
                      <Text fontSize="xs" fontWeight="bold" color="red.900" mb={1}>Mitigation Urgency:</Text>
                      <Text fontSize="xs" color="red.800" whiteSpace="pre-wrap" wordBreak="break-word">{ticketDetails.mitigationUrgency}</Text>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* Root Cause Analysis */}
              <Card bg="orange.50" border="1px" borderColor="orange.200" borderRadius="xl" shadow="sm">
                <CardBody p={4}>
                  <Flex align="center" gap={3} mb={3}>
                    <Box 
                      w={6} 
                      h={6} 
                      bg="orange.500" 
                      borderRadius="lg" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Icon as={FiCode} w={3} h={3} color="white" />
                    </Box>
                    <Heading size="sm" color="orange.900">Root Cause Analysis</Heading>
                  </Flex>
                  <Text color="orange.800" lineHeight="relaxed" fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                    {ticketDetails.rootCause}
                  </Text>
                  {ticketDetails.contributingFactors && Array.isArray(ticketDetails.contributingFactors) && (
                    <Box mt={3}>
                      <Text fontSize="xs" fontWeight="bold" color="orange.900" mb={2}>Contributing Factors:</Text>
                      <List spacing={1}>
                        {ticketDetails.contributingFactors.map((factor, index) => (
                          <ListItem key={index} fontSize="xs" color="orange.800">
                            <ListIcon as={FiAlertCircle} color="orange.500" />
                            <Text whiteSpace="pre-wrap" wordBreak="break-word">{factor}</Text>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardBody>
              </Card>
            </VStack>

            {/* Right Column - Metrics & Details */}
            <VStack spacing={4} align="stretch">
              {/* Recurrence Risk */}
              <Card bg="yellow.50" border="1px" borderColor="yellow.200" borderRadius="xl" shadow="sm">
                <CardBody p={4}>
                  <Flex align="center" gap={3} mb={3}>
                    <Box 
                      w={6} 
                      h={6} 
                      bg="yellow.500" 
                      borderRadius="lg" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Icon as={FiTrendingUp} w={3} h={3} color="white" />
                    </Box>
                    <Heading size="sm" color="yellow.900">Recurrence Risk</Heading>
                  </Flex>
                  <Box mb={3}>
                    <Text fontSize="xs" fontWeight="medium" color="yellow.700" textTransform="uppercase" mb={2}>
                      Probability: {ticketDetails.recurrenceProbability}%
                    </Text>
                    <Progress 
                      value={ticketDetails.recurrenceProbability} 
                      colorScheme={getRecurrenceColorScheme(ticketDetails.recurrenceProbability)}
                      size="sm"
                      mb={2}
                    />
                    <Text color="yellow.800" fontSize="xs" lineHeight="relaxed" whiteSpace="pre-wrap" wordBreak="break-word">
                      {safeToString(ticketDetails.recurrencePattern)}
                    </Text>
                  </Box>
                </CardBody>
              </Card>

              {/* Technical Details */}
              <Card bg="gray.50" border="1px" borderColor="gray.200" borderRadius="xl" shadow="sm">
                <CardBody p={4}>
                  <Flex align="center" gap={3} mb={3}>
                    <Box 
                      w={6} 
                      h={6} 
                      bg="gray.500" 
                      borderRadius="lg" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Icon as={FiSettings} w={3} h={3} color="white" />
                    </Box>
                    <Heading size="sm" color="gray.900">Technical Details</Heading>
                  </Flex>
                  <VStack spacing={2} align="stretch">
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="gray.700" textTransform="uppercase" mb={1}>
                        Error Code
                      </Text>
                      <Text fontSize="xs" color="gray.800" fontFamily="mono" wordBreak="break-all">
                        {safeToString(ticketDetails.techDetails.errorCode)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="gray.700" textTransform="uppercase" mb={1}>
                        Affected Endpoints
                      </Text>
                      <VStack align="start" spacing={1}>
                        {ticketDetails.techDetails.affectedEndpoints.map((endpoint, index) => (
                          <Text key={index} fontSize="xs" color="gray.800" fontFamily="mono" wordBreak="break-all">
                            {safeToString(endpoint)}
                          </Text>
                        ))}
                      </VStack>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="gray.700" textTransform="uppercase" mb={1}>
                        Environments
                      </Text>
                      <HStack spacing={1} wrap="wrap">
                        {ticketDetails.techDetails.environmentsAffected.map((env, index) => (
                          <Badge key={index} colorScheme="orange" variant="subtle" fontSize="xs">
                            {safeToString(env)}
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="gray.700" textTransform="uppercase" mb={1}>
                        Fix ETA
                      </Text>
                      <Text fontSize="xs" color="gray.800" wordBreak="break-word">
                        {safeToString(ticketDetails.techDetails.fixEta)}
                      </Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>

              {/* Affected Systems */}
              <Card bg="blue.50" border="1px" borderColor="blue.200" borderRadius="xl" shadow="sm">
                <CardBody p={4}>
                  <Flex align="center" gap={3} mb={3}>
                    <Box 
                      w={6} 
                      h={6} 
                      bg="blue.500" 
                      borderRadius="lg" 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center"
                    >
                      <Icon as={FiGitBranch} w={3} h={3} color="white" />
                    </Box>
                    <Heading size="sm" color="blue.900">Affected Systems</Heading>
                  </Flex>
                  <VStack spacing={3} align="stretch">
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="blue.700" textTransform="uppercase" mb={2}>
                        Modules
                      </Text>
                      <HStack spacing={2} wrap="wrap">
                        {ticketDetails.modulesAffected.map((module, index) => (
                          <Badge key={index} colorScheme="blue" variant="subtle" fontSize="xs" maxW="full">
                            <Text noOfLines={1}>{safeToString(module)}</Text>
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="blue.700" textTransform="uppercase" mb={2}>
                        Customer Segments
                      </Text>
                      <HStack spacing={2} wrap="wrap">
                        {ticketDetails.customersAffected.map((customer, index) => (
                          <Badge key={index} colorScheme="green" variant="subtle" fontSize="xs" maxW="full">
                            <Text noOfLines={1}>{safeToString(customer)}</Text>
                          </Badge>
                        ))}
                      </HStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </Grid>

          {/* Additional Details - Collapsible Sections */}
          <Accordion allowMultiple>
            {/* Proposed Solution */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Flex align="center" gap={3}>
                    <Icon as={FiCheckCircle} color="green.500" />
                    <Heading size="sm" color="green.900">Proposed Solution</Heading>
                  </Flex>
                </Box>
                <AccordionIcon />
              </AccordionButton>
                             <AccordionPanel pb={4}>
                 <Card bg="green.50" border="1px" borderColor="green.200" borderRadius="lg">
                   <CardBody p={4}>
                     {(() => {
                       const fixDetails = ticketDetails.developerAnalysis.fixDetails;
                       
                       // If it's an object with structured actions
                       if (typeof fixDetails === 'object' && fixDetails !== null && !Array.isArray(fixDetails)) {
                         return (
                           <VStack spacing={4} align="stretch">
                             {fixDetails.emergency && Array.isArray(fixDetails.emergency) && fixDetails.emergency.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="red.700" mb={2}>🚨 Emergency Actions:</Text>
                                 <List spacing={1}>
                                   {fixDetails.emergency.map((action, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.800">
                                       <ListIcon as={FiAlertTriangle} color="red.500" />
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{action}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                             
                             {fixDetails.investigation && Array.isArray(fixDetails.investigation) && fixDetails.investigation.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={2}>🔍 Investigation Actions:</Text>
                                 <List spacing={1}>
                                   {fixDetails.investigation.map((action, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.800">
                                       {/* <ListIcon as={FiCode} color="blue.500" /> */}
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{action}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                             
                             {fixDetails.temporaryWorkarounds && Array.isArray(fixDetails.temporaryWorkarounds) && fixDetails.temporaryWorkarounds.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="orange.700" mb={2}>⚡ Temporary Workarounds:</Text>
                                 <List spacing={1}>
                                   {fixDetails.temporaryWorkarounds.map((action, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.800">
                                       {/* <ListIcon as={FiZap} color="orange.500" /> */}
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{action}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                             
                             {fixDetails.stakeholderNotification && Array.isArray(fixDetails.stakeholderNotification) && fixDetails.stakeholderNotification.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="purple.700" mb={2}>📢 Stakeholder Notifications:</Text>
                                 <List spacing={1}>
                                   {fixDetails.stakeholderNotification.map((action, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.800">
                                       {/* <ListIcon as={FiUsers} color="purple.500" /> */}
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{action}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                           </VStack>
                         );
                       }
                       
                       // If it's an array, display as simple list
                       if (Array.isArray(fixDetails)) {
                         return (
                           <List spacing={2}>
                             {fixDetails.map((action, index) => (
                               <ListItem key={index} fontSize="sm" color="green.800">
                                 <ListIcon as={FiCheckCircle} color="green.500" />
                                 <Text whiteSpace="pre-wrap" wordBreak="break-word">{action}</Text>
                               </ListItem>
                             ))}
                           </List>
                         );
                       }
                       
                       // If it's a string, display as text
                       return (
                         <Text color="green.800" lineHeight="relaxed" fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                           {safeToString(fixDetails)}
                         </Text>
                       );
                     })()}
                   </CardBody>
                 </Card>
               </AccordionPanel>
            </AccordionItem>

            {/* Alternative Theories */}
            {ticketDetails.alternativeTheories && Array.isArray(ticketDetails.alternativeTheories) && (
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Flex align="center" gap={3}>
                      <Icon as={FiAlertCircle} color="orange.500" />
                      <Heading size="sm" color="orange.900">Alternative Theories</Heading>
                    </Flex>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Card bg="orange.50" border="1px" borderColor="orange.200" borderRadius="lg">
                    <CardBody p={4}>
                      <List spacing={3}>
                        {ticketDetails.alternativeTheories.map((theory, index) => (
                          <ListItem key={index} fontSize="sm" color="orange.800">
                            <ListIcon as={FiAlertTriangle} color="orange.500" />
                            <Text whiteSpace="pre-wrap" wordBreak="break-word">{theory}</Text>
                          </ListItem>
                        ))}
                      </List>
                    </CardBody>
                  </Card>
                </AccordionPanel>
              </AccordionItem>
            )}

            {/* Escalation Triggers */}
            {ticketDetails.escalationTriggers && Array.isArray(ticketDetails.escalationTriggers) && (
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Flex align="center" gap={3}>
                      <Icon as={FiAlertTriangle} color="red.500" />
                      <Heading size="sm" color="red.900">Escalation Triggers</Heading>
                    </Flex>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Card bg="red.50" border="1px" borderColor="red.200" borderRadius="lg">
                    <CardBody p={4}>
                      <List spacing={2}>
                        {ticketDetails.escalationTriggers.map((trigger, index) => (
                          <ListItem key={index} fontSize="sm" color="red.800">
                            <ListIcon as={FiAlertCircle} color="red.500" />
                            <Text whiteSpace="pre-wrap" wordBreak="break-word">{trigger}</Text>
                          </ListItem>
                        ))}
                      </List>
                    </CardBody>
                  </Card>
                </AccordionPanel>
              </AccordionItem>
            )}

            {/* QA Testing Plan */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Flex align="center" gap={3}>
                    <Icon as={FiCode} color="gray.500" />
                    <Heading size="sm" color="gray.900">QA Testing Plan</Heading>
                  </Flex>
                </Box>
                <AccordionIcon />
              </AccordionButton>
                             <AccordionPanel pb={4}>
                 <Card bg="gray.900" border="1px" borderColor="gray.700" borderRadius="lg">
                   <CardBody p={4}>
                     {(() => {
                       const qaData = ticketDetails.techDetails.reproductionSteps;
                       
                       // If it's an object with structured QA testing categories
                       if (typeof qaData === 'object' && qaData !== null && !Array.isArray(qaData)) {
                         return (
                           <VStack spacing={3} align="stretch">
                             {qaData.regressionTesting && Array.isArray(qaData.regressionTesting) && qaData.regressionTesting.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="yellow.400" mb={2}>🔄 Regression Testing:</Text>
                                 <List spacing={1}>
                                   {qaData.regressionTesting.map((test, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.400">
                                       <ListIcon as={FiCode} color="yellow.400" />
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{test}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                             
                             {qaData.integrationTesting && Array.isArray(qaData.integrationTesting) && qaData.integrationTesting.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="blue.400" mb={2}>🔗 Integration Testing:</Text>
                                 <List spacing={1}>
                                   {qaData.integrationTesting.map((test, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.400">
                                       <ListIcon as={FiCode} color="blue.400" />
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{test}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                             
                             {qaData.performanceTesting && Array.isArray(qaData.performanceTesting) && qaData.performanceTesting.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="orange.400" mb={2}>⚡ Performance Testing:</Text>
                                 <List spacing={1}>
                                   {qaData.performanceTesting.map((test, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.400">
                                       <ListIcon as={FiCode} color="orange.400" />
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{test}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                             
                             {qaData.userAcceptanceTesting && Array.isArray(qaData.userAcceptanceTesting) && qaData.userAcceptanceTesting.length > 0 && (
                               <Box>
                                 <Text fontSize="sm" fontWeight="bold" color="purple.400" mb={2}>👥 User Acceptance Testing:</Text>
                                 <List spacing={1}>
                                   {qaData.userAcceptanceTesting.map((test, index) => (
                                     <ListItem key={index} fontSize="sm" color="green.400">
                                       <ListIcon as={FiCode} color="purple.400" />
                                       <Text whiteSpace="pre-wrap" wordBreak="break-word">{test}</Text>
                                     </ListItem>
                                   ))}
                                 </List>
                               </Box>
                             )}
                           </VStack>
                         );
                       }
                       
                       // If it's a string, format it into a readable list
                       if (typeof qaData === 'string') {
                         // Split by common delimiters and clean up
                         const steps = qaData
                           .split(/[.!?]\s+/)
                           .map(step => step.trim())
                           .filter(step => step.length > 0)
                           .map(step => step.replace(/^[A-Z]/, step[0].toUpperCase())); // Capitalize first letter
                         
                         return (
                           <VStack spacing={2} align="stretch">
                             {steps.map((step, index) => (
                               <Box key={index}>
                                 <HStack spacing={2} align="start">
                                   <Text color="green.400" fontSize="sm" fontWeight="bold" minW="20px">
                                     {index + 1}.
                                   </Text>
                                   <Text 
                                     color="green.400" 
                                     fontSize="sm" 
                                     lineHeight="relaxed"
                                     wordBreak="break-word"
                                   >
                                     {step}
                                   </Text>
                                 </HStack>
                               </Box>
                             ))}
                           </VStack>
                         );
                       }
                       
                       // If it's an array, display as list
                       if (Array.isArray(qaData)) {
                         return (
                           <VStack spacing={2} align="stretch">
                             {qaData.map((step, index) => (
                               <Box key={index}>
                                 <HStack spacing={2} align="start">
                                   <Text color="green.400" fontSize="sm" fontWeight="bold" minW="20px">
                                     {index + 1}.
                                   </Text>
                                   <Text 
                                     color="green.400" 
                                     fontSize="sm" 
                                     lineHeight="relaxed"
                                     wordBreak="break-word"
                                   >
                                     {step}
                                   </Text>
                                 </HStack>
                               </Box>
                             ))}
                           </VStack>
                         );
                       }
                       
                       // Fallback to pre-formatted text
                       return (
                         <Text 
                           as="pre" 
                           color="green.400" 
                           fontSize="sm" 
                           fontFamily="mono" 
                           whiteSpace="pre-wrap" 
                           lineHeight="relaxed"
                           wordBreak="break-word"
                         >
                           {qaData}
                         </Text>
                       );
                     })()}
                   </CardBody>
                 </Card>
               </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </VStack>
      </Box>
    );
  } catch (error) {
    console.error('Error rendering BugTicketDetails:', error);
    return (
      <Box p={6} bg="red.50" border="1px" borderColor="red.200" borderRadius="lg">
        <Text color="red.800" fontWeight="bold">Error rendering bug details:</Text>
        <Text color="red.700" fontSize="sm" mt={2}>{error.message}</Text>
        <Text color="red.600" fontSize="xs" mt={2}>Please check the console for more details.</Text>
      </Box>
    );
  }
}
